import {
  PaymentAndSend,
  Payment,
  PaymentsStore,
  TippingPreference,
  OnlinePaymentSendResponse,
  PaymentApiOptions,
  PaymentFilterCondition,
  PaymentSetupResponse,
  PaymentProcessStates,
  PreviousLocationAndTerminal,
  SendReceiptPayload,
  TimelineEntry,
  FirestoreTimestampLike,
} from "@/payments/types/payments";
import { defineStore } from "pinia";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import axios from "@/app/plugins/axios";
import { db } from "@/app/plugins/firebase";
import { onSnapshot, collection, query, where, orderBy } from "firebase/firestore";
import { PaymentStatus } from "@/shared/enums/status.enum";
import {
  getPaymentDisplayStatus,
  getPaymentRefundStatus,
} from "@/payments/utils/paymentStatus.utils";
import i18n from "@/app/plugins/i18n";
import { getErrorMessage } from "@/shared/utils/errorMessage";
import { isApiErrorResponse } from "@/shared/utils/apiError.utils";

const toUnixSeconds = (value: FirestoreTimestampLike | null | undefined): number => {
  if (value === null || value === undefined) {
    return 0;
  }
  if (typeof value === "number") {
    return value > 1e12 ? Math.floor(value / 1000) : Math.floor(value);
  }
  if (typeof value === "string") {
    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) {
      return numericValue > 1e12 ? Math.floor(numericValue / 1000) : Math.floor(numericValue);
    }
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return Math.floor(parsed / 1000);
    }
    return 0;
  }
  if (value instanceof Date) {
    return Math.floor(value.getTime() / 1000);
  }
  if ("seconds" in value && typeof value.seconds === "number") {
    return Math.floor(value.seconds);
  }
  if ("_seconds" in value && typeof value._seconds === "number") {
    return Math.floor(value._seconds);
  }
  if ("toDate" in value && typeof value.toDate === "function") {
    return Math.floor(value.toDate().getTime() / 1000);
  }
  return 0;
};

const normalizeTimelineEntry = (entry: TimelineEntry): TimelineEntry => ({
  ...entry,
  timestamp: toUnixSeconds(entry?.time),
});

const getDefaultTippingPreference = (): TippingPreference => ({
  includeTip: false,
  locationId: null,
});

export const usePaymentStore = defineStore("payment", {
  state: (): PaymentsStore => ({
    payments: [],
    selectedPayment: null,
    clearedFilter: false,
    filterPaymentCondition: {},
    loading: false,
    listLoading: false,
    listRequestId: 0,
    succeeded: false,
    authorized: false,
    canceled: false,
    realTimeLine: [],
    unsubscribe: null,
    activeTimelinePaymentIntentId: null,
    activeTimelineAccountId: null,
    noResults: false,
    hasMore: false,
    startingAfter: "",
    endingBefore: "",
    nextPage: "",
    searchCurrentPage: "",
    isPaymentLinkPayment: false,
    formCompleted: false,
    formFailed: false,
    transactionCompleted: false,
    isCartPayment: false,
    refundTimeline: [],
    paymentProcessStates: {},
    tippingPreference: getDefaultTippingPreference(),
  }),

  getters: {
    isLoading: (state) => state.loading,
    isListLoading: (state) => state.listLoading,
    getSelectedPayment: (state) => state.selectedPayment,
    getPayments: (state) => state.payments,
  },

  actions: {
    setSelectedPayment(payment: Payment | null): void {
      this.selectedPayment = payment;
    },
    clearTimeline(): void {
      this.realTimeLine = [];
      this.refundTimeline = [];
    },
    setFilterPaymentCondition(condition: PaymentFilterCondition): void {
      this.filterPaymentCondition = condition;
    },
    setClearAllFilter(status: boolean): void {
      this.clearedFilter = status;
    },
    setNewPayments(options: { preserveTimelinePaymentIntentId?: string | null } = {}): void {
      const shouldPreserveTimelineListener =
        !!options.preserveTimelinePaymentIntentId &&
        this.activeTimelinePaymentIntentId === String(options.preserveTimelinePaymentIntentId);

      if (!shouldPreserveTimelineListener) {
        if (this.unsubscribe) {
          this.unsubscribe();
          this.unsubscribe = null;
        }
        this.activeTimelinePaymentIntentId = null;
        this.activeTimelineAccountId = null;
      }
      this.paymentProcessStates = {};
      this.realTimeLine = [];
      this.transactionCompleted = false;
      this.canceled = false;
      this.succeeded = false;
      this.authorized = false;
      this.selectedPayment = null;
      this.formCompleted = false;
      this.formFailed = false;
    },
    setTippingPreference(locationId: string | null, includeTip: boolean): void {
      const resolvedLocation = locationId ?? null;
      this.tippingPreference = {
        locationId: resolvedLocation,
        includeTip: includeTip && !!resolvedLocation,
      };
    },
    async createPayment(payload: PaymentAndSend, terminalId: string): Promise<Payment> {
      const snackBarStore = useSnackBarStore();
      if (!terminalId) {
        const errorMessage = "Terminal ID is required to create a payment.";
        snackBarStore.toastError({ message: errorMessage });
        throw new Error(errorMessage);
      }
      try {
        const response = await axios.post<Payment>(`/payment/create/${terminalId}`, payload);
        return response.data;
      } catch (error) {
        const err = error as { data?: { message?: string }; message?: string };
        snackBarStore.toastError(
          err?.data || { message: err?.message || "Failed to create payment." },
        );
        throw error;
      }
    },
    async createOnlinePayment(payload: PaymentAndSend): Promise<OnlinePaymentSendResponse> {
      const response = await axios.post<OnlinePaymentSendResponse>(`/payment/online`, payload);
      return response.data;
    },
    setPaymentProcessStates(states: PaymentProcessStates): void {
      this.paymentProcessStates = {
        ...this.paymentProcessStates,
        ...states,
      };
    },
    setTransactionCompleted(status: boolean): void {
      this.transactionCompleted = status;
    },

    async confirmZeroDollarCartOrder(paymentId: string): Promise<Payment> {
      const response = await axios.post<Payment>(`/cart/${paymentId}/confirm-zero`);
      const payment = response.data;
      this.selectedPayment = payment;
      this.setPaymentProcessStates({ paymentIntent: payment });
      this.setTransactionCompleted(true);
      this.succeeded = true;
      return payment;
    },

    async captureAuthorizedPayment(paymentId: string, signal?: AbortSignal): Promise<Payment> {
      const response = await axios.post<Payment>(`/payment/${paymentId}/capture`, {}, { signal });
      return response.data;
    },

    async cancelAuthorizedPayment(paymentId: string, signal?: AbortSignal): Promise<Payment> {
      const response = await axios.post<Payment>(`/payment/${paymentId}/cancel`, {}, { signal });
      return response.data;
    },

    async retrievePaymentForAction(paymentId: string, signal?: AbortSignal): Promise<Payment> {
      const response = await axios.get<Payment>(`/payment/retrieve/${paymentId}`, { signal });
      return response.data;
    },

    async cancelPayment(paymentId: string, terminalId?: string): Promise<void> {
      try {
        this.loading = true;

        const response = await axios.post<Payment>(`/payment/${paymentId}/cancel`, { terminalId });

        // If the selected payment is the one being canceled, update the selected payment
        if (this.selectedPayment?.id === paymentId) {
          this.selectedPayment = response.data;
        }

        // Check if the payment exists in the payments array
        const paymentIndex = this.payments.findIndex((payment) => payment.id === paymentId);
        if (paymentIndex !== -1) {
          // Replace the payment object with the updated one from the response
          if (response?.data) {
            this.payments[paymentIndex] = response.data;
          } else {
            // If no response data, update the status to canceled
            this.payments[paymentIndex] = {
              ...this.payments[paymentIndex],
              status: PaymentStatus.CANCELED,
            };
          }
        }
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
      } finally {
        this.loading = false;
      }
    },
    async fetchPayments(apiOptions?: PaymentApiOptions): Promise<boolean> {
      const requestId = ++this.listRequestId;
      this.listLoading = true;
      try {
        const response = await axios.get<{ data: Payment[]; has_more: boolean }>(
          "/payment",
          apiOptions,
        );
        if (requestId !== this.listRequestId) return false;
        const paymentsFetched = response.data?.data ?? [];
        this.hasMore = response.data.has_more;
        this.noResults = paymentsFetched.length === 0;
        this.payments = [...paymentsFetched];
        return true;
      } catch (error) {
        if (requestId !== this.listRequestId) return false;
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
        return false;
      } finally {
        if (requestId === this.listRequestId) this.listLoading = false;
      }
    },

    async getFilteredPayments(apiOptions?: PaymentApiOptions): Promise<boolean> {
      const requestId = ++this.listRequestId;
      try {
        // Start by considering all payments
        this.listLoading = true;
        this.noResults = false;

        // If there are no conditions, then all payments are "filtered"
        if (Object.keys(this.filterPaymentCondition).length === 0) {
          const response = await axios.get<{ data: Payment[]; has_more: boolean }>(
            "/payment",
            apiOptions,
          );
          if (requestId !== this.listRequestId) return false;
          const paymentsFetched = response.data?.data ?? [];
          this.hasMore = response.data.has_more;
          this.nextPage = undefined;
          this.noResults = paymentsFetched.length === 0;
          this.payments = [...paymentsFetched];
          return true;
        }

        // Prepare an array to store the filter conditions
        const filters: string[] = [];
        // Check location condition
        if (this.filterPaymentCondition.location) {
          filters.push(`metadata["location"]:'${this.filterPaymentCondition.location}'`);
        }

        // Check terminal condition
        if (this.filterPaymentCondition.terminal) {
          filters.push(`metadata["terminal"]:'${this.filterPaymentCondition.terminal}'`);
        }

        // Check minAmount condition
        if (this.filterPaymentCondition.minAmount) {
          filters.push(`amount>=${this.filterPaymentCondition.minAmount * 100}`);
        }

        // Check maxAmount condition
        if (this.filterPaymentCondition.maxAmount) {
          filters.push(`amount<=${this.filterPaymentCondition.maxAmount * 100}`);
        }

        // Check startDate condition
        if (this.filterPaymentCondition.startDate) {
          filters.push(`created>=${this.filterPaymentCondition.startDate}`);
        }

        // Check endDate condition
        if (this.filterPaymentCondition.endDate) {
          // add 24 hours to include the endDate in search, hours * minutes * seconds
          filters.push(`created<${this.filterPaymentCondition.endDate + 24 * 60 * 60}`);
        }

        // Check status condition
        if (this.filterPaymentCondition.status) {
          //Check refunded / partially refunded status, set to succeeded status first
          if (
            this.filterPaymentCondition.status === "refunded" ||
            this.filterPaymentCondition.status === "partially_refunded"
          ) {
            filters.push(`status:'succeeded'`);
          } else {
            filters.push(`status:'${this.filterPaymentCondition.status}'`);
          }
        }

        // Check productId condition
        if (this.filterPaymentCondition.productId) {
          filters.push(`metadata["productId"]:'${this.filterPaymentCondition.productId}'`);
        }

        // Check priceId condition
        if (this.filterPaymentCondition.priceId) {
          filters.push(`metadata["priceId"]:'${this.filterPaymentCondition.priceId}'`);
        }

        //check payment links
        if (this.filterPaymentCondition.paymentLink) {
          filters.push(`metadata["payment_link"]:"${this.filterPaymentCondition.paymentLink}"`);
        }

        //check customer
        if (this.filterPaymentCondition.customer) {
          filters.push(`customer:'${this.filterPaymentCondition.customer}'`);
        }

        // Combine the filters with "AND" operator
        const combinedFilter = filters.join(" AND ");

        if (combinedFilter) {
          const payload: { query: string; limit?: number; page?: string } = {
            query: combinedFilter,
            limit: apiOptions?.params?.limit,
          };

          if (apiOptions?.params?.page) {
            payload.page = apiOptions?.params?.page;
          }

          const response = await axios.post<{
            data: Payment[];
            has_more: boolean;
            next_page: string;
          }>("/payment/search", payload);
          if (requestId !== this.listRequestId) return false;
          const filteredPayments = response.data?.data ?? [];
          this.hasMore = response.data.has_more;
          this.nextPage = response.data.next_page;
          this.noResults = filteredPayments.length === 0;
          // Expand charge obj of payment, check if it's refund or partially_refund
          if (this.filterPaymentCondition.status === "refunded") {
            this.payments = filteredPayments.filter(
              (payment) => getPaymentRefundStatus(payment) === PaymentStatus.REFUNDED,
            );
          } else if (this.filterPaymentCondition.status === "partially_refunded") {
            this.payments = filteredPayments.filter(
              (payment) => getPaymentRefundStatus(payment) === PaymentStatus.PARTIALLY_REFUNDED,
            );
          } else if (this.filterPaymentCondition.status === "succeeded") {
            this.payments = filteredPayments.filter((payment) => {
              const latestCharge =
                typeof payment?.latest_charge === "object" ? payment.latest_charge : null;
              return latestCharge?.amount_refunded === 0;
            });
          } else {
            this.payments = [...filteredPayments];
          }
          return true;
        }
        return false;
      } catch (error) {
        if (requestId !== this.listRequestId) return false;
        const data = isApiErrorResponse(error) ? error.data : undefined;
        const code =
          (data && typeof data === "object" ? data.code : undefined) ??
          (error && typeof error === "object" && "code" in error ? error.code : undefined);
        const translationKey = typeof code === "string" ? `page.payment.filtersError.${code}` : "";
        useSnackBarStore().toastError({
          message:
            translationKey && i18n.global.te(translationKey)
              ? i18n.global.t(translationKey)
              : getErrorMessage(error),
        });
        return false;
      } finally {
        if (requestId === this.listRequestId) this.listLoading = false;
      }
    },

    async retrievePayment(paymentId: string): Promise<Payment | undefined> {
      try {
        this.loading = true;
        const response = await axios.get<Payment>(`/payment/retrieve/${paymentId}`);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
      } finally {
        this.loading = false;
      }
    },

    async retrieveRefundPayment(paymentId: string): Promise<Payment | undefined> {
      /* Retrieve refund payment is used to get a payment without setting loading to true. This is
      because refunding is handled using a modal whereby we don't want the parent page to reload*/
      try {
        const response = await axios.get<Payment>(`/payment/retrieve/${paymentId}`);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
      }
    },

    async getSinglePayment(paymentId: string | string[]): Promise<void> {
      try {
        this.isPaymentLinkPayment = false;
        this.loading = true;
        const response = await axios.get<Payment>(`/payment/retrieve/${paymentId}`);
        const payment = response.data;
        this.selectedPayment = payment;
        this.isPaymentLinkPayment = payment?.paymentLinkPayment;
        this.isCartPayment = payment?.metadata?.isCart === "true";
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
      } finally {
        this.loading = false;
      }
    },

    async getPaymentProcessStates(
      paymentIntentId?: string,
      previousLocationAndTerminal: PreviousLocationAndTerminal = {},
    ): Promise<PaymentSetupResponse | undefined> {
      try {
        this.loading = true;
        // Construct the query string dynamically
        const params: Record<string, string> = {};
        if (paymentIntentId) {
          params.paymentIntentId = paymentIntentId;
        }

        // Use axios to send the GET request with query parameters
        const response = await axios.get<PaymentSetupResponse>(`/payment/setup`, { params });
        const paymentProcessStates = response.data;
        const firstLocation = paymentProcessStates.terminalsAndLocations?.locations?.[0];

        this.paymentProcessStates = {
          location:
            paymentProcessStates?.terminalsAndLocations?.default_location?.id ||
            previousLocationAndTerminal.location ||
            firstLocation?.id ||
            null,
          terminal:
            paymentProcessStates?.terminalsAndLocations?.default_terminal?.terminalId ||
            previousLocationAndTerminal.terminal ||
            firstLocation?.terminals?.[0]?.terminalId ||
            null,
          form: paymentProcessStates.forms?.default_form || {
            id: null,
            name: "No Form",
            type: "",
          },
          customer:
            typeof paymentProcessStates?.customer === "object" &&
            paymentProcessStates?.customer !== null
              ? (paymentProcessStates?.customer?.id ?? null)
              : (paymentProcessStates?.customer ?? null),
          terminalObj:
            paymentProcessStates?.terminalsAndLocations?.default_terminal ||
            previousLocationAndTerminal.terminalObj ||
            firstLocation?.terminals?.[0] ||
            null,
          locationObj:
            paymentProcessStates?.terminalsAndLocations?.default_location ||
            previousLocationAndTerminal.locationObj ||
            firstLocation ||
            null,
          paymentIntent: paymentProcessStates?.paymentIntent || null,
        };

        return paymentProcessStates;
      } catch (error) {
        useSnackBarStore().toastError({
          message:
            (error as { response?: { data?: { error?: string } } }).response?.data?.error ||
            "An error occurred",
        });
      } finally {
        this.loading = false;
      }
    },

    getPaymentStatus(payment: Payment | null | undefined): string | null | undefined {
      return getPaymentDisplayStatus(payment);
    },

    async addPaymentNote(paymentId: string, note: { note: string }): Promise<Payment | undefined> {
      try {
        const response = await axios.post<Payment>(`/payment/note/${paymentId}`, note);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
      }
    },

    async updatePaymentAndSend(
      terminalId: string,
      paymentId: string,
      payload: PaymentAndSend,
    ): Promise<Payment | undefined> {
      try {
        this.loading = true;
        const response = await axios.put<Payment>(
          `/payment/update/${terminalId}/${paymentId}`,
          payload,
        );
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async updateOnlinePayment(
      paymentId: string,
      payload: PaymentAndSend,
    ): Promise<OnlinePaymentSendResponse> {
      try {
        this.loading = true;
        const response = await axios.patch<OnlinePaymentSendResponse>(
          `/payment/online/${paymentId}`,
          payload,
        );
        return response.data;
      } finally {
        this.loading = false;
      }
    },

    async resendOnlinePayment(
      paymentId: string,
      receiptEmail?: string,
    ): Promise<OnlinePaymentSendResponse> {
      try {
        this.loading = true;
        const payload = receiptEmail ? { receipt_email: receiptEmail } : {};
        const response = await axios.post<OnlinePaymentSendResponse>(
          `/payment/online/resend/${paymentId}`,
          payload,
        );
        return response.data;
      } finally {
        this.loading = false;
      }
    },

    isKnownPaymentStatus(key: string): key is "succeeded" | "canceled" {
      return ["succeeded", "canceled"].includes(key);
    },
    isKnowFormStatus(key: string): key is "formCompleted" | "formFailed" {
      return ["formCompleted", "formFailed"].includes(key);
    },
    isKnownStatus(
      key: string,
    ): key is "succeeded" | "canceled" | "transactionCompleted" | "formCompleted" | "formFailed" {
      return [
        "succeeded",
        "canceled",
        "transactionCompleted",
        "formCompleted",
        "formFailed",
      ].includes(key);
    },

    async listenToPayments(accountId: string, paymentIntentId: string): Promise<void> {
      if (!accountId || !paymentIntentId) return;
      const activePaymentIntentId = String(paymentIntentId);
      const timelineRef = collection(db, "accounts", accountId, "timelines");
      const q = query(timelineRef, where("pid", "==", paymentIntentId), orderBy("time", "desc"));
      this.activeTimelinePaymentIntentId = activePaymentIntentId;
      this.activeTimelineAccountId = accountId;
      this.realTimeLine = [];

      this.unsubscribe = onSnapshot(q, (snapshot) => {
        if (
          this.activeTimelinePaymentIntentId !== activePaymentIntentId ||
          this.activeTimelineAccountId !== accountId
        ) {
          return;
        }

        this.realTimeLine = snapshot.docs.map((doc) =>
          normalizeTimelineEntry(doc.data() as TimelineEntry),
        );

        // Authorization ends collection, but must never set paid/form completion flags.
        const matchingTimeline = this.realTimeLine.filter(
          (entry) => entry.pid === activePaymentIntentId,
        );
        const hasFinalPaymentEvent = matchingTimeline.some(
          (entry) =>
            entry.event === "payment_intent.succeeded" ||
            entry.event === "payment_intent.canceled" ||
            entry.status === "succeeded" ||
            entry.status === "canceled",
        );
        const currentPaymentIsFinal =
          this.selectedPayment?.id === activePaymentIntentId &&
          (this.selectedPayment.status === "succeeded" ||
            this.selectedPayment.status === "canceled");
        this.authorized =
          !currentPaymentIsFinal &&
          !hasFinalPaymentEvent &&
          matchingTimeline.some(
            (entry) => entry.event === "payment_intent.amount_capturable_updated",
          );

        if (
          this.authorized &&
          this.selectedPayment?.id === activePaymentIntentId &&
          this.selectedPayment.status !== "succeeded" &&
          this.selectedPayment.status !== "canceled"
        ) {
          this.selectedPayment.status = "requires_capture";
        }

        snapshot.docChanges().forEach((change) => {
          const newData = normalizeTimelineEntry(change.doc.data() as TimelineEntry);

          // To prevent blocking PaymentProcessPage from showing the confirmation dialog
          if (newData.status === "succeeded") {
            /**
             * TODO: Tech Debt - This is required for multiple flows listening for success.
             */
            if (this.selectedPayment) {
              this.selectedPayment.status = "succeeded";
            }
            this.succeeded = true;
          }

          if (newData.status && this.isKnownStatus(newData.status)) {
            switch (newData.status) {
              case "succeeded":
                this.succeeded = true;
                break;
              case "canceled":
                this.canceled = true;
                break;
              case "transactionCompleted":
                this.transactionCompleted = true;
                break;
              case "formCompleted":
                this.formCompleted = true;
                break;
              case "formFailed":
                this.formFailed = true;
                break;
              default:
                break;
            }
          }

          const type = this.paymentProcessStates?.form?.type;
          // no form selected, listen to payment status
          if (!type) {
            if (newData.status && this.isKnownPaymentStatus(newData.status)) {
              this.unsubscribe?.();
            }
          } else {
            // in any cases, canceled event should unsubscribe
            if (newData.status === "transactionCompleted" || newData.status === "canceled") {
              this.unsubscribe?.();
            }
          }
        });
      });
    },

    async listenToNotePayments(accountId: string, paymentIntentId: string): Promise<void> {
      if (!accountId || !paymentIntentId) return;
      const timelineRef = collection(db, "accounts", accountId, "timelines");
      const q = query(timelineRef, where("pid", "==", paymentIntentId), orderBy("time", "desc"));
      this.unsubscribe = onSnapshot(q, (snapshot) => {
        const noteEntries = snapshot.docs
          .map((doc) => normalizeTimelineEntry(doc.data() as TimelineEntry))
          .filter((entry) => entry.event === "note_created");
        this.realTimeLine = noteEntries;
      });
    },

    async listenToRefundPayments(accountId: string, paymentIntentId: string): Promise<void> {
      this.refundTimeline = [];
      if (!accountId || !paymentIntentId) return;
      const timelineRef = collection(db, "accounts", accountId, "timelines");
      const q = query(timelineRef, where("pid", "==", paymentIntentId), orderBy("time", "desc"));
      // store the current timestamp
      const startTime = Math.floor(Date.now() / 1000);
      onSnapshot(q, (snapshot) => {
        const refundTimeline = snapshot.docs
          .map((doc) => normalizeTimelineEntry(doc.data() as TimelineEntry))
          .filter(
            (data) =>
              (data?.event?.startsWith("refunded_") ?? false) &&
              toUnixSeconds(data?.time) > startTime,
          );
        this.refundTimeline = [...refundTimeline];
      });
    },

    unsubscribeListener(): void {
      if (this.unsubscribe) {
        this.unsubscribe();
        this.unsubscribe = null;
      }
    },

    async sendReceipt(data: SendReceiptPayload): Promise<void> {
      const { email, paymentIntentId } = data;

      const payload = {
        email,
      };

      try {
        this.loading = true;
        await axios.post(`/payment/${paymentIntentId}/receipt`, payload);
        useSnackBarStore().toastSuccess({
          message: i18n.global.t("page.paymentDetails.receiptSentSuccess"),
        });
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
      } finally {
        this.loading = false;
      }
    },
  },
});
